// ============================================================
// PLATEAU 建物 MVT (Mapbox Vector Tile) クライアント
// ============================================================
// データソース: Indigo Lab がホストする PLATEAU 2020 建物 MVT。
//   https://indigo-lab.github.io/plateau-tokyo23ku-building-mvt-2020/{z}/{x}/{y}.pbf
//
// 本番では CloudFront の /plateau-buildings/* behavior でリバースプロキシして同じパス形式
// にする。dev は Vite proxy で同じ。
//
// 機能:
// - 表示 bbox から必要な (z, x, y) を計算
// - 各タイルを fetch → @mapbox/vector-tile でパース → polygon の lon/lat 配列を抽出
// - in-memory + localStorage キャッシュ (TTL 7 日)
// - z は radius から自動選択 (500m/1km → 15, 2km/3km → 14)
//   5km/10km は呼び出し側でスキップ

import { VectorTile } from '@mapbox/vector-tile'
import Protobuf from 'pbf'

export interface BuildingShape {
  /** 多角形のリング群 (外側 + 穴)。各リングは [lon, lat] の配列で閉じない (start ≠ end) */
  rings: [number, number][][]
}

export type LogFn = (msg: string) => void

interface CachedTile {
  shapes: BuildingShape[]
  fetchedAt: number
}

const TILE_CACHE_PREFIX = 'plateau-buildings-v1:'
const TILE_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** タイル URL のテンプレート。{z}{x}{y} を埋め込む */
const TILE_URL_TEMPLATE =
  // dev は同一オリジン (/plateau-buildings/{z}/{x}/{y}.pbf) で proxy
  // prd は CloudFront の同パス
  '/plateau-buildings/{z}/{x}/{y}.pbf'

/** 同時 fetch 上限 (タイル数が多いときに DoS しない) */
const MAX_CONCURRENT_FETCH = 4

export class BuildingTileStore {
  private mem = new Map<string, BuildingShape[]>()
  private inflight = new Map<string, Promise<BuildingShape[]>>()
  private logger: LogFn = () => {}
  private origin = ''
  /** 永続的に空 (404 や zero-feature) と確認したタイル */
  private knownEmpty = new Set<string>()

  setLogger(fn: LogFn): void { this.logger = fn }
  setOrigin(origin: string): void { this.origin = origin }

  /**
   * 指定 bbox に重なる建物 polygon を返す。
   * @param zoom 0–16 程度。実際の zoom は呼び出し側で radius から決定する想定
   */
  async buildingsInBBox(
    minLon: number, minLat: number, maxLon: number, maxLat: number, zoom: number,
  ): Promise<BuildingShape[]> {
    const tiles = tileRangeForBBox(minLon, minLat, maxLon, maxLat, zoom)
    if (tiles.length > 64) {
      // セーフティ: 64タイル超は zoom out させる
      this.logger(`Buildings: too many tiles at z=${zoom} (${tiles.length}), skipping`)
      return []
    }

    // 並列フェッチ (上限あり)
    const out: BuildingShape[] = []
    const queue = [...tiles]
    const workers: Promise<void>[] = []
    for (let i = 0; i < Math.min(MAX_CONCURRENT_FETCH, queue.length); i++) {
      workers.push((async () => {
        while (queue.length > 0) {
          const t = queue.shift()
          if (!t) break
          const shapes = await this.getTile(t.z, t.x, t.y)
          out.push(...shapes)
        }
      })())
    }
    await Promise.all(workers)
    return out
  }

  // ───────── private ─────────

  private async getTile(z: number, x: number, y: number): Promise<BuildingShape[]> {
    const key = `${z}/${x}/${y}`
    if (this.knownEmpty.has(key)) return []
    const m = this.mem.get(key)
    if (m) return m
    const flight = this.inflight.get(key)
    if (flight) return flight

    const p = (async () => {
      // localStorage キャッシュ
      const cached = readCache(key)
      if (cached && Date.now() - cached.fetchedAt < TILE_TTL_MS) {
        this.mem.set(key, cached.shapes)
        if (cached.shapes.length === 0) this.knownEmpty.add(key)
        return cached.shapes
      }

      const url = this.origin + TILE_URL_TEMPLATE
        .replace('{z}', String(z))
        .replace('{x}', String(x))
        .replace('{y}', String(y))
      try {
        const res = await fetch(url)
        if (res.status === 404) {
          // 範囲外タイル → 永久に empty
          this.knownEmpty.add(key)
          writeCache(key, { shapes: [], fetchedAt: Date.now() })
          this.mem.set(key, [])
          return []
        }
        if (!res.ok) {
          this.logger(`Building tile ${key} failed: ${res.status}`)
          return []
        }
        const buf = new Uint8Array(await res.arrayBuffer())
        const shapes = parseTile(buf, z, x, y)
        if (shapes.length === 0) this.knownEmpty.add(key)
        this.mem.set(key, shapes)
        writeCache(key, { shapes, fetchedAt: Date.now() })
        return shapes
      } catch (err) {
        this.logger(`Building tile ${key} err: ${(err as Error).message}`)
        return []
      } finally {
        this.inflight.delete(key)
      }
    })()
    this.inflight.set(key, p)
    return p
  }
}

// ───────── helpers ─────────

interface TileCoord { z: number; x: number; y: number }

/** 指定 bbox と zoom で重なるタイル座標の集合を返す */
function tileRangeForBBox(
  minLon: number, minLat: number, maxLon: number, maxLat: number, z: number,
): TileCoord[] {
  const xMin = lonToTileX(minLon, z)
  const xMax = lonToTileX(maxLon, z)
  // 北のほうが y は小さい
  const yMin = latToTileY(maxLat, z)
  const yMax = latToTileY(minLat, z)
  const out: TileCoord[] = []
  for (let x = xMin; x <= xMax; x++) {
    for (let y = yMin; y <= yMax; y++) {
      out.push({ z, x, y })
    }
  }
  return out
}

function lonToTileX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, z))
}

function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z),
  )
}

/**
 * MVT バイナリをパースして building polygon の lon/lat 表現を抽出。
 * Indigo Lab のタイルは layer 名 "bldg" もしくは "building" が想定だが、
 * 念のため全 layer をスキャンして type=POLYGON の feature を採用する。
 */
function parseTile(buf: Uint8Array, z: number, x: number, y: number): BuildingShape[] {
  const tile = new VectorTile(new Protobuf(buf))
  const shapes: BuildingShape[] = []
  for (const layerName of Object.keys(tile.layers)) {
    const layer = tile.layers[layerName]
    if (!layer) continue
    for (let i = 0; i < layer.length; i++) {
      // 個別 feature の例外で tile 全体を捨てたくないので局所 try/catch
      try {
        const f = layer.feature(i)
        // 1 = Point, 2 = LineString, 3 = Polygon
        // (PLATEAU タイルには稀に type=4 の拡張 feature が混入することがあるが、
        //  我々は polygon のみ使うので無視する)
        if (f.type !== 3) continue
        const geo = f.toGeoJSON(x, y, z)
        const g = geo.geometry
        if (g.type === 'Polygon') {
          shapes.push({ rings: g.coordinates as [number, number][][] })
        } else if (g.type === 'MultiPolygon') {
          for (const poly of g.coordinates as [number, number][][][]) {
            shapes.push({ rings: poly })
          }
        }
      } catch {
        // skip just this one feature
        continue
      }
    }
  }
  return shapes
}

function readCache(key: string): CachedTile | null {
  try {
    const raw = localStorage.getItem(TILE_CACHE_PREFIX + key)
    if (!raw) return null
    return JSON.parse(raw) as CachedTile
  } catch {
    return null
  }
}

function writeCache(key: string, v: CachedTile): void {
  try {
    localStorage.setItem(TILE_CACHE_PREFIX + key, JSON.stringify(v))
  } catch {
    // QuotaExceeded など — 無視
  }
}

/**
 * radius (m) から適切な MVT zoom を返す。null は "建物表示なし" の意。
 * - 500m  → z=16 (1タイル~610m, 1〜4タイル)
 * - 1km   → z=15 (1タイル~1.2km, 4〜9タイル)
 * - 5km〜 → 表示しない (ピクセルが足りない & タイル数膨張)
 */
export function zoomForRadius(radiusM: number): number | null {
  if (radiusM <= 600) return 16
  if (radiusM <= 1500) return 15
  if (radiusM <= 3000) return 14
  return null
}
